/**
 * HealthTicker.jsx
 *
 * Auto-scrolling horizontal marquee ticker with click-to-open detail modals.
 *
 * Tuning constants:
 *   SCROLL_SPEED_S  – seconds per full seamless loop (increase = slower)
 *   REFRESH_MS      – API re-fetch interval in milliseconds
 */

import React, {
  useState, useEffect, useRef, useCallback, useMemo, Component
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { endpoints } from '../api/config';
import useHealthAlarm from '../hooks/useHealthAlarm';

// ─── Tuning constants ─────────────────────────────────────────────────────────
const SCROLL_SPEED_S = 40;
const REFRESH_MS     = 60000;
const HEALTH_API_URL = `${import.meta.env.VITE_API_URL}${endpoints.jyHealth}`;

// ─── Palette ──────────────────────────────────────────────────────────────────
const CLR = {
  healthy: '#10b981', error: '#ef4444', warn: '#f59e0b',
  bg: '#f8fafc', border: '#e2e8f0',
  text: '#0f172a', muted: '#64748b', subtle: '#94a3b8',
};

// ─── Pure mapping: API JSON → ticker items ────────────────────────────────────
export function mapHealthToItems(data) {
  if (!data) return [];
  const items = [];

  const ec2 = data.ec2;
  if (ec2) {
    const ok = ec2.status === 'healthy' && ec2.total_instances > 0;
    items.push({
      id: 'ec2', label: 'EC2',
      value: ok ? `${ec2.running_instances} of ${ec2.total_instances}` : 'Unavailable',
      sub: 'instances running',
      borderColor: ok ? CLR.healthy : CLR.error, dotColor: ok ? CLR.healthy : CLR.error,
    });
  }

  const rds = data.rds;
  if (rds) {
    const ok = rds.status === 'healthy' && rds.total_databases > 0;
    items.push({
      id: 'rds', label: 'Database',
      value: ok ? `${rds.available_databases} of ${rds.total_databases}` : 'Unavailable',
      sub: 'available',
      borderColor: ok ? CLR.healthy : CLR.error, dotColor: ok ? CLR.healthy : CLR.error,
    });
  }

  const region = data.region_health;
  if (region) {
    const ok = region.status === 'healthy';
    const ms = typeof region.latency_ms === 'number'
      ? `${Math.round(region.latency_ms).toLocaleString()} ms` : 'Unavailable';
    const reg  = region.region || 'us-east-1';
    const code = region.http_status ? `HTTP ${region.http_status}` : '';
    items.push({
      id: 'region', label: 'Region',
      value: ms, sub: code ? `${reg}, ${code}` : reg,
      borderColor: ok ? CLR.healthy : CLR.warn, dotColor: ok ? CLR.healthy : CLR.warn,
    });
  }

  const ns = data.netsuite_endpoints;
  if (ns) {
    const ok  = ns.status === 'healthy';
    const val = ns.summary || (typeof ns.total === 'number'
      ? `${ns.successful ?? 0}/${ns.total}` : 'Unavailable');
    items.push({
      id: 'netsuite', label: 'NetSuite',
      value: val, sub: 'endpoints passing',
      borderColor: ok ? CLR.healthy : CLR.error, dotColor: ok ? CLR.healthy : CLR.error,
    });
  }

  const sync = data.priority_sync;
  if (sync) {
    const ok       = sync.status === 'healthy';
    const notSynced = typeof sync.not_synced === 'number'
      ? sync.not_synced.toLocaleString() : 'Unavailable';
    const noLabel   = typeof sync.no_label === 'number' ? sync.no_label : 0;
    items.push({
      id: 'prioritySync', label: 'Priority Sync',
      value: notSynced, sub: `waiting to sync · ${noLabel} no label`,
      borderColor: ok ? CLR.healthy : CLR.warn, dotColor: ok ? CLR.healthy : CLR.warn,
    });
  }

  const epList = data.netsuite_endpoints?.endpoints;
  if (Array.isArray(epList)) {
    epList.forEach((ep, i) => {
      const ok  = ep.is_success === true;
      const sec = typeof ep.execution_time_seconds === 'number'
        ? `${ep.execution_time_seconds.toFixed(2)}s` : '—';
      items.push({
        id: `ep::${ep.endpoint || i}`,
        label: ep.endpoint || `Endpoint ${i + 1}`,
        value: sec, sub: ep.http_status ? `HTTP ${ep.http_status}` : '—',
        borderColor: ok ? CLR.healthy : CLR.error, dotColor: ok ? CLR.healthy : CLR.error,
      });
    });
  }

  return items;
}

// ─── Pure detail builder ──────────────────────────────────────────────────────
/**
 * getHealthDetail(itemId, healthData) → { title, statusColor, summaryRows[], tables[], notes[] }
 * Pure function — easy to unit-test.
 */
export function getHealthDetail(itemId, data) {
  if (!data) return null;

  const badge = (s) => {
    if (!s) return { label: 'Unknown', color: CLR.warn };
    const l = s.toLowerCase();
    if (l === 'healthy' || l === 'success') return { label: 'Healthy', color: CLR.healthy };
    if (l === 'unhealthy' || l === 'failed' || l === 'error') return { label: 'Unhealthy', color: CLR.error };
    return { label: s, color: CLR.warn };
  };

  // ── EC2 ──────────────────────────────────────────────────────────────────────
  if (itemId === 'ec2') {
    const ec2 = data.ec2;
    if (!ec2) return { title: 'EC2', badge: badge(null), summaryRows: [], tables: [], notes: ['No longer available'] };
    const instances = Array.isArray(ec2.instances) ? ec2.instances : [];
    return {
      title: 'EC2 Instances',
      badge: badge(ec2.status),
      summaryRows: [
        { label: 'Running', value: `${ec2.running_instances ?? 0} / ${ec2.total_instances ?? 0}` },
        ...(ec2.reason ? [{ label: 'Reason', value: ec2.reason }] : []),
      ],
      tables: instances.length > 0
        ? [{ title: 'Instance List', rows: instances }]
        : [],
      notes: instances.length === 0 ? [ec2.reason || 'No instances returned'] : [],
    };
  }

  // ── RDS ──────────────────────────────────────────────────────────────────────
  if (itemId === 'rds') {
    const rds = data.rds;
    if (!rds) return { title: 'Database', badge: badge(null), summaryRows: [], tables: [], notes: ['No longer available'] };
    const dbs = Array.isArray(rds.databases) ? rds.databases : [];
    return {
      title: 'Database (RDS)',
      badge: badge(rds.status),
      summaryRows: [
        { label: 'Available', value: `${rds.available_databases ?? 0} / ${rds.total_databases ?? 0}` },
        ...(rds.reason ? [{ label: 'Reason', value: rds.reason }] : []),
      ],
      tables: dbs.length > 0 ? [{ title: 'Database List', rows: dbs }] : [],
      notes: dbs.length === 0 ? [rds.reason || 'No databases returned'] : [],
    };
  }

  // ── Region ───────────────────────────────────────────────────────────────────
  if (itemId === 'region') {
    const r = data.region_health;
    if (!r) return { title: 'Region', badge: badge(null), summaryRows: [], tables: [], notes: ['No longer available'] };
    return {
      title: 'Region Health',
      badge: badge(r.status),
      summaryRows: [
        { label: 'Region', value: r.region || 'us-east-1' },
        { label: 'Latency', value: typeof r.latency_ms === 'number' ? `${r.latency_ms.toLocaleString(undefined, { maximumFractionDigits: 2 })} ms` : '—' },
        { label: 'HTTP Status', value: r.http_status ? String(r.http_status) : '—' },
        ...(r.reason ? [{ label: 'Reason', value: r.reason }] : []),
      ],
      tables: [],
      notes: [],
    };
  }

  // ── NetSuite (summary) ────────────────────────────────────────────────────────
  if (itemId === 'netsuite') {
    const ns = data.netsuite_endpoints;
    if (!ns) return { title: 'NetSuite', badge: badge(null), summaryRows: [], tables: [], notes: ['No longer available'] };
    const epRows = Array.isArray(ns.endpoints) ? ns.endpoints : [];
    const failed = Array.isArray(ns.failed_endpoints) ? ns.failed_endpoints : [];
    return {
      title: 'NetSuite Endpoints',
      badge: badge(ns.status),
      summaryRows: [
        { label: 'Summary', value: ns.summary || `${ns.successful ?? 0}/${ns.total ?? 0}` },
        { label: 'Total', value: String(ns.total ?? 0) },
        { label: 'Successful', value: String(ns.successful ?? 0) },
        { label: 'Failed', value: String(ns.failed ?? 0), highlight: (ns.failed ?? 0) > 0 ? CLR.error : null },
      ],
      failedEndpoints: failed,
      tables: epRows.length > 0 ? [{
        title: 'All Endpoints',
        columns: ['Endpoint', 'Status', 'HTTP', 'Exec (s)', 'Last Updated'],
        rows: epRows.map(ep => ({
          _highlight: ep.is_success === false ? 'rgba(239,68,68,0.06)' : null,
          Endpoint: ep.endpoint,
          Status: ep.status,
          HTTP: ep.http_status,
          'Exec (s)': typeof ep.execution_time_seconds === 'number' ? ep.execution_time_seconds.toFixed(2) : '—',
          'Last Updated': ep.updated_at ? fmtDateTime(ep.updated_at) : '—',
        })),
      }] : [],
      notes: [],
    };
  }

  // ── Single NetSuite endpoint ──────────────────────────────────────────────────
  if (itemId.startsWith('ep::')) {
    const epPath = itemId.slice(4);
    const epList = data.netsuite_endpoints?.endpoints;
    const ep = Array.isArray(epList) ? epList.find(e => (e.endpoint || '') === epPath) : null;
    if (!ep) return { title: epPath || 'Endpoint', badge: badge(null), summaryRows: [], tables: [], notes: ['No longer available'] };

    const execS  = typeof ep.execution_time_seconds === 'number' ? ep.execution_time_seconds : null;
    let execFmt  = execS !== null ? `${execS.toFixed(2)}s` : '—';
    if (execS !== null && execS >= 60) {
      const m = Math.floor(execS / 60); const s = Math.round(execS % 60);
      execFmt = `${execS.toFixed(2)}s (${m}m ${s}s)`;
    }
    const updatedAt  = ep.updated_at || null;
    const updatedFmt = updatedAt ? fmtDateTime(updatedAt) : '—';
    const relTime    = updatedAt ? relativeTime(updatedAt) : null;
    const stale      = updatedAt ? isOlderThan6Hours(updatedAt) : false;

    return {
      title: ep.endpoint || 'Endpoint',
      badge: badge(ep.is_success ? 'success' : 'failed'),
      summaryRows: [
        { label: 'Endpoint', value: ep.endpoint || '—' },
        { label: 'Status', value: ep.status || '—' },
        { label: 'HTTP Status', value: ep.http_status ? String(ep.http_status) : '—' },
        { label: 'Execution Time', value: execFmt },
        { label: 'Last Updated', value: `${updatedFmt}${relTime ? ` (${relTime})` : ''}` },
      ],
      tables: [],
      notes: [],
      staleWarning: stale ? 'Last update was more than 6 hours ago — data may be stale.' : null,
    };
  }

  // ── Priority Sync ─────────────────────────────────────────────────────────────
  if (itemId === 'prioritySync') {
    const s = data.priority_sync;
    if (!s) return { title: 'Priority Sync', badge: badge(null), summaryRows: [], tables: [], notes: ['No longer available'] };
    return {
      title: 'Priority Sync',
      badge: badge(s.status),
      summaryRows: [
        { label: 'Waiting to sync', value: typeof s.not_synced === 'number' ? s.not_synced.toLocaleString() : '—' },
        { label: 'No label', value: typeof s.no_label === 'number' ? String(s.no_label) : '—' },
      ],
      tables: [],
      notes: [],
      showPriorityLink: true,
    };
  }

  // ── API error ─────────────────────────────────────────────────────────────────
  if (itemId === '__err__') {
    return {
      title: 'Health API Unreachable',
      badge: { label: 'Error', color: CLR.error },
      summaryRows: [],
      tables: [],
      notes: ['The health API could not be reached. Check connectivity or the API server.'],
      isError: true,
    };
  }

  return null;
}

// ─── Date helpers ─────────────────────────────────────────────────────────────
function fmtDateTime(str) {
  try {
    const d = new Date(str.includes('T') ? str : str.replace(' ', 'T') + 'Z');
    if (isNaN(d)) return str;
    return d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
  } catch { return str; }
}

function relativeTime(str) {
  try {
    const d = new Date(str.includes('T') ? str : str.replace(' ', 'T') + 'Z');
    if (isNaN(d)) return null;
    const diffMs  = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1)  return 'just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24)   return `${diffH}h ago`;
    return `${Math.round(diffH / 24)}d ago`;
  } catch { return null; }
}

function isOlderThan6Hours(str) {
  try {
    const d = new Date(str.includes('T') ? str : str.replace(' ', 'T') + 'Z');
    return Date.now() - d.getTime() > 6 * 3600 * 1000;
  } catch { return false; }
}

// ─── Inject keyframes & ticker styles ─────────────────────────────────────────
function injectKeyframes() {
  const id = 'health-ticker-keyframes';
  if (typeof document === 'undefined') return;
  let s = document.getElementById(id);
  if (!s) {
    s = document.createElement('style');
    s.id = id;
    document.head.appendChild(s);
  }
  s.textContent = `
    @keyframes ht-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
    .health-track, .ht-track {
      display: inline-flex;
      align-items: center;
      width: max-content;
      padding: 0 14px;
      animation: ht-scroll ${SCROLL_SPEED_S}s linear infinite;
      will-change: transform;
    }
    /* Pure CSS pause on outer container hover, keyboard focus-visible, and manual pause class */
    .health-ticker:hover .health-track,
    .health-ticker:has(:focus-visible) .health-track,
    .health-ticker.paused .health-track,
    .health-viewport:hover .health-track,
    .health-viewport:has(:focus-visible) .health-track,
    .health-track.paused,
    .ht-ticker:hover .ht-track,
    .ht-ticker:has(:focus-visible) .ht-track,
    .ht-ticker.paused .ht-track,
    .ht-viewport:hover .ht-track,
    .ht-viewport:has(:focus-visible) .ht-track,
    .ht-track.paused {
      animation-play-state: paused !important;
    }
    @media (prefers-reduced-motion: reduce) {
      .health-track, .ht-track { animation: none !important; }
      .ht-bell-pulse { animation: none !important; }
    }
    @keyframes ht-bell-pulse {
      0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
      50% { transform: scale(1.15); box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
      100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
    }
    .ht-bell-pulse {
      animation: ht-bell-pulse 1s ease-in-out infinite;
      border-color: #ef4444 !important;
      color: #ef4444 !important;
    }
    .ht-card-btn:hover  { background: #f1f5f9 !important; transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.10) !important; }
    .ht-card-btn:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; }
    .ht-bell-btn:hover { transform: translateY(-1px); box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
    .ht-bell-btn:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; }
  `;
}
if (typeof document !== 'undefined') {
  injectKeyframes();
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
const Badge = ({ label, color }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 10px', borderRadius: 12,
    background: `${color}18`, border: `1px solid ${color}55`,
    fontSize: 12, fontWeight: 700, color,
  }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
    {label}
  </span>
);

// ─── Dynamic key-value table from array of objects ────────────────────────────
const DynamicTable = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  const keys = Object.keys(rows[0]).filter(k => k !== '_highlight');
  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {keys.map(k => (
              <th key={k} style={{ padding: '6px 10px', textAlign: 'left', background: '#f1f5f9', color: CLR.muted, fontWeight: 600, borderBottom: `1px solid ${CLR.border}`, whiteSpace: 'nowrap' }}>
                {String(k).toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: row._highlight || (i % 2 === 0 ? '#fff' : '#fafafa') }}>
              {keys.map(k => (
                <td key={k} style={{ padding: '6px 10px', borderBottom: `1px solid ${CLR.border}`, color: CLR.text, wordBreak: 'break-word', maxWidth: 240 }}>
                  {row[k] !== null && row[k] !== undefined ? String(row[k]) : '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Modal ────────────────────────────────────────────────────────────────────
const HealthDetailModal = ({ itemId, healthData, timestamp, onClose, onRetry }) => {
  const navigate    = useNavigate();
  const modalRef    = useRef(null);
  const closeRef    = useRef(null);
  const reduced     = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const detail = useMemo(() => getHealthDetail(itemId, healthData), [itemId, healthData]);

  // Focus trap + Esc
  useEffect(() => {
    const prev = document.activeElement;
    closeRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = Array.from(
          modalRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
        ).filter(el => !el.disabled);
        if (!focusable.length) { e.preventDefault(); return; }
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const origOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = origOverflow;
      prev?.focus();
    };
  }, [onClose]);

  if (!detail) return null;

  const fmtTs = timestamp ? fmtDateTime(timestamp) : null;

  return createPortal(
    <div
      role="presentation"
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)',
        animation: reduced ? 'none' : 'ht-modal-in 0.18s ease',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={detail.title}
        style={{
          background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          width: '100%', maxWidth: 680, maxHeight: '82vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 12px', borderBottom: `1px solid ${CLR.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: CLR.text }}>{detail.title}</span>
            <Badge label={detail.badge.label} color={detail.badge.color} />
          </div>
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label="Close dialog"
            style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: CLR.muted, flexShrink: 0 }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* Stale warning */}
          {detail.staleWarning && (
            <div style={{ background: `${CLR.warn}15`, border: `1px solid ${CLR.warn}55`, borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#92400e', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 15 }}>⚠️</span>
              <span>{detail.staleWarning}</span>
            </div>
          )}

          {/* Summary rows */}
          {detail.summaryRows && detail.summaryRows.length > 0 && (
            <div style={{ background: '#f8fafc', borderRadius: 8, border: `1px solid ${CLR.border}`, marginBottom: 16, overflow: 'hidden' }}>
              {detail.summaryRows.map((row, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 12, padding: '9px 14px',
                  borderBottom: i < detail.summaryRows.length - 1 ? `1px solid ${CLR.border}` : 'none',
                  background: i % 2 === 0 ? '#fff' : '#f8fafc',
                }}>
                  <span style={{ fontSize: 12, color: CLR.muted, minWidth: 130, flexShrink: 0, fontWeight: 600 }}>{row.label}</span>
                  <span style={{ fontSize: 12, color: row.highlight || CLR.text, fontWeight: row.highlight ? 700 : 400, wordBreak: 'break-word' }}>{row.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Failed endpoints (NetSuite) */}
          {detail.failedEndpoints && detail.failedEndpoints.length > 0 && (
            <div style={{ background: `${CLR.error}08`, border: `1px solid ${CLR.error}30`, borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: CLR.error, marginBottom: 6 }}>Failed Endpoints</div>
              {detail.failedEndpoints.map((ep, i) => (
                <div key={i} style={{ fontSize: 12, color: CLR.error, padding: '2px 0' }}>• {String(ep)}</div>
              ))}
            </div>
          )}

          {/* Tables */}
          {detail.tables && detail.tables.map((tbl, ti) => (
            <div key={ti} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: CLR.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tbl.title}</div>
              <DynamicTable rows={tbl.rows} />
            </div>
          ))}

          {/* Notes / empty states */}
          {detail.notes && detail.notes.map((note, i) => (
            <div key={i} style={{ fontSize: 12, color: CLR.muted, background: '#f8fafc', border: `1px solid ${CLR.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 10 }}>
              {note}
            </div>
          ))}

          {/* Priority sync link */}
          {detail.showPriorityLink && (
            <button
              onClick={() => { onClose(); navigate('/dashboard'); }}
              style={{ marginTop: 8, padding: '8px 16px', background: CLR.healthy, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              Open Priority Dashboard →
            </button>
          )}

          {/* Error retry */}
          {detail.isError && (
            <button
              onClick={onRetry}
              style={{ marginTop: 8, padding: '8px 16px', background: CLR.error, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              Retry
            </button>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${CLR.border}`, padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: '#fafafa' }}>
          <span style={{ fontSize: 11, color: CLR.subtle }}>
            {fmtTs ? `Last checked: ${fmtTs}` : 'Fetching…'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/system-health');
              }}
              style={{ padding: '5px 14px', background: '#f1f5f9', border: `1px solid ${CLR.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12, color: CLR.text, fontWeight: 600 }}
            >
              View details
            </button>
            <button
              type="button"
              onClick={onRetry}
              style={{ padding: '5px 14px', background: '#f1f5f9', border: `1px solid ${CLR.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12, color: CLR.text, fontWeight: 600 }}
            >
              Refresh now
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Error Boundary ──────────────────────────────────────────────────────────
export class HealthTickerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('HealthTicker error caught by boundary:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px',
            background: '#fef2f2',
            border: `1px solid ${CLR.error}40`,
            borderRadius: 8,
            color: CLR.error,
            fontSize: 13,
            height: 56,
            boxSizing: 'border-box',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>⚠️</span>
            <span style={{ fontWeight: 600 }}>Health details failed to load</span>
          </div>
          <button
            type="button"
            onClick={this.handleRetry}
            style={{
              padding: '5px 12px',
              background: '#fff',
              border: `1px solid ${CLR.error}66`,
              borderRadius: 6,
              color: CLR.error,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Single ticker card (interactive) ────────────────────────────────────────
const TickerCard = React.memo(({ id, label, value, sub, borderColor, dotColor, isDupe, onClick }) => {
  const handleKey = (e) => {
    if (!isDupe && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(id); }
  };
  return (
    <button
      type="button"
      role="button"
      className="ht-card-btn"
      aria-label={isDupe ? undefined : `View ${label} details`}
      aria-hidden={isDupe ? 'true' : undefined}
      tabIndex={isDupe ? -1 : 0}
      onClick={() => onClick(id)}
      onKeyDown={handleKey}
      style={{
        display: 'inline-flex', flexDirection: 'column', justifyContent: 'center',
        padding: '4px 16px 4px 10px',
        border: 'none',
        borderLeft: `3px solid ${borderColor}`,
        marginRight: 18,
        background: '#fff',
        borderRadius: 6,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        minWidth: 108,
        flexShrink: 0,
        userSelect: 'none',
        lineHeight: 1,
        cursor: 'pointer',
        transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
        <span style={{ fontSize: 9.5, fontWeight: 700, color: CLR.muted, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: CLR.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
        {value}
      </div>
      <div style={{ fontSize: 10, color: CLR.subtle, whiteSpace: 'nowrap', marginTop: 2, lineHeight: 1.2 }}>
        {sub}
      </div>
    </button>
  );
});
TickerCard.displayName = 'TickerCard';

// ─── HealthTicker ─────────────────────────────────────────────────────────────
const HealthTicker = ({ isPaused: manualPaused = false }) => {
  const [healthData, setHealthData] = useState(null);
  const [items,      setItems]      = useState([]);
  const [timestamp,  setTimestamp]  = useState(null);
  const [errored,    setErrored]    = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [openItemId, setOpenItemId] = useState(null); // which modal is open (null = closed)

  const trackRef    = useRef(null);
  const abortRef    = useRef(null);
  const reduced     = useRef(
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );

  const {
    alarmEnabled,
    isRinging,
    suspendedHint,
    bannerInfo,
    toggleAlarm,
    acknowledge,
    dismissBanner,
  } = useHealthAlarm(healthData);

  injectKeyframes();

  // ── fetch ────────────────────────────────────────────────────────────────────
  const fetchHealth = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res  = await fetch(HEALTH_API_URL, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const mapped = mapHealthToItems(json);
      if (mapped.length > 0) {
        setHealthData(json);
        setItems(mapped);
        setTimestamp(json.timestamp || new Date().toISOString());
        setErrored(false);
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const iv = setInterval(fetchHealth, REFRESH_MS);
    return () => { clearInterval(iv); abortRef.current?.abort(); };
  }, [fetchHealth]);

  // Combined paused state: paused if modal is open or manual pause is enabled
  const isPaused = Boolean(openItemId || manualPaused);

  // ── display list ─────────────────────────────────────────────────────────────
  const baseList = loading
    ? Array.from({ length: 5 }, (_, i) => ({
        id: `sk_${i}`, label: '···', value: '—', sub: '···',
        borderColor: CLR.border, dotColor: CLR.border,
      }))
    : [
        ...items,
        ...(errored ? [{
          id: '__err__', label: 'Health API', value: '!', sub: 'unreachable',
          borderColor: CLR.error, dotColor: CLR.error,
        }] : []),
      ];

  const copies = reduced.current ? 1 : 2;

  const handleCardClick = useCallback((id) => {
    if (id.startsWith('sk_')) return; // skeleton, ignore
    setOpenItemId(id);
  }, []);

  const handleClose = useCallback(() => setOpenItemId(null), []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
      {/* Alert banner if an item turned unhealthy */}
      {bannerInfo && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '4px 12px',
            marginBottom: 6,
            background: '#fef2f2',
            border: `1px solid ${CLR.error}44`,
            borderLeft: `4px solid ${CLR.error}`,
            borderRadius: 6,
            color: '#991b1b',
            fontSize: 12,
            boxSizing: 'border-box',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden' }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>🚨</span>
            <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {bannerInfo.names} turned unhealthy
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: '#b91c1c', opacity: 0.85 }}>
              {bannerInfo.time}
            </span>
            <button
              type="button"
              onClick={dismissBanner}
              aria-label="Dismiss banner"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#991b1b',
                fontSize: 13,
                padding: '0 2px',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Outer non-moving container with overflow: hidden */}
      <div
        className={`health-ticker ht-ticker ${isPaused ? 'paused' : ''}`}
        style={{
          display: 'flex', alignItems: 'center',
          width: '100%', minWidth: 0,
          background: CLR.bg, borderRadius: 8,
          border: `1px solid ${CLR.border}`,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          height: 56, position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Left fade */}
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 20, background: `linear-gradient(to right, ${CLR.bg} 40%, transparent)`, zIndex: 2, pointerEvents: 'none' }} />

        {/* Scroll viewport */}
        <div
          className="health-viewport ht-viewport"
          style={{ flex: 1, minWidth: 0, overflow: reduced.current ? 'auto' : 'hidden', position: 'relative' }}
        >
          <div
            ref={trackRef}
            className="health-track ht-track"
            style={{
              display: 'inline-flex', alignItems: 'center',
              width: 'max-content', padding: '0 14px',
            }}
          >
            {Array.from({ length: copies }, (_, ci) =>
              baseList.map(item => (
                <TickerCard
                  key={`${ci}_${item.id}`}
                  id={item.id}
                  label={item.label}
                  value={item.value}
                  sub={item.sub}
                  borderColor={item.borderColor}
                  dotColor={item.dotColor}
                  isDupe={ci > 0}
                  onClick={handleCardClick}
                />
              ))
            )}
          </div>
          {/* Right fade inside viewport */}
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 24, background: `linear-gradient(to left, ${CLR.bg} 40%, transparent)`, zIndex: 2, pointerEvents: 'none' }} />
        </div>

        {/* Right controls: Bell toggle + Acknowledge button + Autoplay hint */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 10px',
            background: CLR.bg,
            zIndex: 3,
            flexShrink: 0,
            height: '100%',
            borderLeft: `1px solid ${CLR.border}`,
          }}
        >
          {/* Autoplay suspended hint */}
          {isRinging && suspendedHint && (
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                color: '#b45309',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                padding: '2px 6px',
                borderRadius: 4,
                whiteSpace: 'nowrap',
              }}
            >
              🔔 Click anywhere to enable alarm sound
            </span>
          )}

          {/* Red Acknowledge button */}
          {isRinging && (
            <button
              type="button"
              onClick={acknowledge}
              aria-label="Acknowledge and silence alarm"
              style={{
                background: CLR.error,
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                letterSpacing: '0.02em',
                boxShadow: '0 1px 3px rgba(239, 68, 68, 0.4)',
                whiteSpace: 'nowrap',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = CLR.error; }}
            >
              Acknowledge
            </button>
          )}

          {/* Bell toggle button: Alarm on/off */}
          <button
            type="button"
            onClick={toggleAlarm}
            aria-label="Alarm on/off"
            aria-pressed={alarmEnabled}
            className={`ht-bell-btn ${isRinging ? 'ht-bell-pulse' : ''}`}
            title={
              isRinging
                ? 'Alarm ringing – click to silence or toggle'
                : alarmEnabled
                ? 'Alarm is ON (click to mute or test beep)'
                : 'Alarm is OFF (click to enable and test sound)'
            }
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              border: `1px solid ${isRinging ? CLR.error : (alarmEnabled ? '#93c5fd' : CLR.border)}`,
              background: isRinging ? '#fee2e2' : (alarmEnabled ? '#eff6ff' : '#fff'),
              color: isRinging ? CLR.error : (alarmEnabled ? '#2563eb' : CLR.muted),
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              flexShrink: 0,
              transition: 'background 0.15s, border-color 0.15s, transform 0.15s',
            }}
          >
            {alarmEnabled ? (
              // Active Bell SVG
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            ) : (
              // Slashed / Muted Bell SVG
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
                <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
                <path d="M18 8a6 6 0 0 0-9.33-5" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Modal wrapped in ErrorBoundary */}
      {openItemId && (
        <HealthTickerErrorBoundary onRetry={() => { setOpenItemId(null); fetchHealth(); }}>
          <HealthDetailModal
            itemId={openItemId}
            healthData={healthData}
            timestamp={timestamp}
            onClose={handleClose}
            onRetry={fetchHealth}
          />
        </HealthTickerErrorBoundary>
      )}
    </div>
  );
};

const HealthTickerWithBoundary = (props) => {
  const [resetKey, setResetKey] = useState(0);
  const handleRetry = useCallback(() => {
    setResetKey(k => k + 1);
  }, []);

  return (
    <HealthTickerErrorBoundary key={resetKey} onRetry={handleRetry}>
      <HealthTicker {...props} />
    </HealthTickerErrorBoundary>
  );
};

export default HealthTickerWithBoundary;
